'use client';

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { trackEvent } from '@/lib/analytics';

export default function ContactSection() {
  const { t } = useLanguage();
  const [formTime, setFormTime] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    business: '',
    solution: 'Onix Presència (des de 390 €)',
    message: '',
    website: '', // honeypot
  });

  useEffect(() => {
    setFormTime(Date.now());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);

    try {
      trackEvent('contact_form_submit_attempt', { solution: formData.solution });

      const data = new FormData();
      data.append('name', formData.name);
      data.append('phone', formData.phone);
      data.append('email', formData.email || 'no-email-proporcionat@onixdigital.pro');
      data.append('companyType', formData.business || 'Negoci local');
      data.append('address', `Solució: ${formData.solution} - Missatge: ${formData.message}`);
      data.append('website', formData.website);
      data.append('_formTime', String(formTime));

      const res = await fetch('/api/contact', {
        method: 'POST',
        body: data,
      });

      if (!res.ok) {
        throw new Error('Error en enviar el formulari');
      }

      setSubmitted(true);
      trackEvent('contact_form_success', { solution: formData.solution });
    } catch (err) {
      console.error(err);
      setErrorMsg('Hi ha hagut un problema en enviar el formulari. Si us plau, escriu-nos directament a WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacte"
      className="py-24 px-6 bg-[#050507] relative overflow-hidden border-t border-white/5"
      aria-label="Contacte i sol·licitud de pressupost"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 right-1/4 w-[600px] h-[400px] bg-purple-brand/10 blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto space-y-16 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-[11px] font-bold uppercase tracking-widest">
            {t.contact.badge}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase">
            {t.contact.title}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            {t.contact.sub}
          </p>
        </div>

        {/* 2-Column Layout: Form + Direct WhatsApp */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start max-w-6xl mx-auto">
          {/* Left Column: Form */}
          <div className="lg:col-span-7 rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-[#111119] to-[#09090e] border border-white/10 shadow-2xl relative">
            {submitted ? (
              <div className="text-center py-12 space-y-6">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 flex items-center justify-center text-3xl mx-auto font-black">
                  ✓
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-white">
                    {t.contact.successTitle}
                  </h3>
                  <p className="text-slate-300 text-sm max-w-md mx-auto">
                    {t.contact.successDesc}
                  </p>
                </div>
                <div className="pt-4">
                  <a
                    href="https://wa.me/34694919757?text=Hola%2C%20acabe%20d%27enviar%20el%20formulari%20a%20la%20web%20i%20volia%20confirmar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 py-3 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider transition-colors"
                  >
                    <span>💬 {t.contact.openWhatsAppBtn}</span>
                  </a>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-xl font-bold text-white pb-2 border-b border-white/10">
                  {t.contact.formTitle}
                </h3>

                {/* Honeypot hidden field */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-300 block">
                      {t.contact.nameLabel} *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={t.contact.namePlaceholder}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-gold text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-300 block">
                      {t.contact.phoneLabel} *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder={t.contact.phonePlaceholder}
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-gold text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-300 block">
                      {t.contact.businessLabel} *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={t.contact.businessPlaceholder}
                      value={formData.business}
                      onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-gold text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-300 block">
                      {t.contact.emailLabel}
                    </label>
                    <input
                      type="email"
                      placeholder="nom@elteunegoci.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-gold text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-300 block">
                    {t.contact.solutionLabel}
                  </label>
                  <select
                    value={formData.solution}
                    onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-[#161622] border border-white/10 text-white focus:outline-none focus:border-gold text-sm"
                  >
                    {t.contact.solutionOptions.map((opt, i) => (
                      <option key={i} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-300 block">
                    {t.contact.messageLabel}
                  </label>
                  <textarea
                    rows={3}
                    placeholder={t.contact.messagePlaceholder}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-gold text-sm"
                  />
                </div>

                {errorMsg && (
                  <p className="text-xs text-rose-400 bg-rose-500/10 p-3 rounded-lg border border-rose-500/20">
                    {errorMsg}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-black bg-gradient-to-r from-gold via-gold-light to-gold-dark hover:brightness-110 shadow-lg shadow-gold/25 transition-all duration-200"
                >
                  {loading ? t.contact.submittingBtn : t.contact.submitBtn}
                </button>

                <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                  {t.contact.privacyNote}
                </p>
              </form>
            )}
          </div>

          {/* Right Column: Direct Contact & WhatsApp */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-3xl p-8 bg-gradient-to-b from-[#141524] to-[#0c0d18] border border-gold/30 space-y-6">
              <span className="text-[10px] font-black uppercase tracking-widest text-gold bg-gold/10 px-3 py-1 rounded-full border border-gold/20 inline-block">
                {t.contact.directBadge}
              </span>

              <div className="space-y-2">
                <h3 className="text-2xl font-black text-white">
                  {t.contact.directTitle}
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                  {t.contact.directDesc}
                </p>
              </div>

              {/* Direct WhatsApp Callout */}
              <div className="pt-2">
                <a
                  href="https://wa.me/34694919757?text=Hola%2C%20m%27agradaria%20informaci%C3%B3%20sobre%20les%20solucions%20digitals%20d%27Onix%20Digital"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full py-4 px-5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs sm:text-sm uppercase tracking-wider shadow-xl shadow-emerald-950/50 transition-all text-center"
                >
                  <span className="text-lg">💬</span>
                  <span>{t.contact.whatsappBtn}</span>
                </a>
              </div>

              <div className="text-center pt-2">
                <a
                  href="tel:+34694919757"
                  className="text-xs text-slate-400 hover:text-white font-bold transition-colors inline-block"
                >
                  {t.contact.callBtn}
                </a>
              </div>
            </div>

            {/* Commitment card */}
            <div className="rounded-2xl p-6 bg-white/5 border border-white/5 space-y-3">
              <div className="flex items-center gap-2 text-gold font-bold text-xs uppercase tracking-wider">
                <span>🛡️</span>
                <span>{t.contact.commitmentTitle}</span>
              </div>
              <ul className="space-y-2 text-xs text-slate-300">
                {t.contact.commitmentPoints.map((pt, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-emerald-400 font-bold">✓</span>
                    <span>{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
